import { useEffect, useRef, useState } from "react";

import {
  ENGAGEMENT_THRESHOLDS,
  engagementFor,
  type UserEngagement
} from "@/lib/refresh-policy";

/**
 * Tracks user engagement for the idle-confirm + refresh-policy system.
 *
 * Returns:
 *   • `engagement` — `"active" | "soft-idle" | "hard-idle"` based on
 *     how long since the last genuine user interaction.
 *   • `lastInteractionAt` — the timestamp of the most recent tracked
 *     event (ms). Exposed so UI can render "about N minutes ago" in
 *     the idle modal.
 *   • `bump()` — imperative handle so a component can mark
 *     engagement reset without faking an input event. Called by the
 *     idle modal when the user taps Refresh, for instance.
 *
 * Events that COUNT as interaction (bubbled at document):
 *   pointerdown, pointermove (500 ms throttle), keydown, scroll,
 *   wheel, touchstart.
 *
 * Events that DO NOT count:
 *   focus / blur, programmatic focus, mutation observer events, or
 *   any DOM change that wasn't user-originated.
 *
 * Sleep-wake watchdog: a 60 s heartbeat compares `Date.now()` deltas.
 * If a tick skipped more than `sleepDetectionMs`, we force
 * `hard-idle` regardless of the last-interaction age — user's
 * laptop probably just woke up and the data on screen is definitely
 * stale relative to the wall clock.
 */
export function useUserEngagement(): {
  engagement: UserEngagement;
  lastInteractionAt: number;
  bump: () => void;
} {
  const [state, setState] = useState<{
    engagement: UserEngagement;
    lastInteractionAt: number;
  }>(() => ({
    engagement: "active",
    lastInteractionAt: typeof performance !== "undefined" ? Date.now() : 0
  }));
  // Mirror in a ref so the event listeners don't need to read stale
  // closure state to decide if the engagement level changed.
  const lastAtRef = useRef(state.lastInteractionAt);
  // Throttle pointermove updates — wrist jitter on a trackpad fires
  // dozens of events per second, which would mask every bug behind
  // "always active".
  const lastMoveWriteRef = useRef(0);

  const bump = () => {
    const now = Date.now();
    lastAtRef.current = now;
    setState((prev) => {
      if (prev.engagement === "active" && prev.lastInteractionAt === now) {
        return prev;
      }
      return { engagement: "active", lastInteractionAt: now };
    });
  };

  useEffect(() => {
    if (typeof document === "undefined") return;

    const recordInteraction = () => {
      const now = Date.now();
      lastAtRef.current = now;
      // Always rewrite state so consumers that read
      // `lastInteractionAt` see the latest timestamp — cheap, React
      // bails on equal engagement values via the setState functional
      // update below.
      setState((prev) =>
        prev.engagement === "active"
          ? { ...prev, lastInteractionAt: now }
          : { engagement: "active", lastInteractionAt: now }
      );
    };

    const onMove = (e: PointerEvent | MouseEvent) => {
      // 500ms floor so a moving cursor doesn't reset the timer every
      // 16ms. Pointerdown / keydown are not throttled — they're
      // intentional and rare.
      const now = Date.now();
      if (now - lastMoveWriteRef.current < 500) return;
      lastMoveWriteRef.current = now;
      recordInteraction();
      void e;
    };

    const onDiscrete = () => recordInteraction();

    // Passive listeners for scroll-y events so iOS Safari can continue
    // to optimise native scroll. Document-level capture so children
    // can't stopPropagation us.
    document.addEventListener("pointerdown", onDiscrete, { passive: true });
    document.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("keydown", onDiscrete, { passive: true });
    document.addEventListener("scroll", onDiscrete, { passive: true, capture: true });
    document.addEventListener("wheel", onDiscrete, { passive: true });
    document.addEventListener("touchstart", onDiscrete, { passive: true });

    // Engagement evaluator + sleep-wake watchdog. Fires every 30s —
    // fine-grained enough that the transition into soft-idle doesn't
    // lag by more than half a minute, coarse enough to cost nothing.
    let lastTickAt = Date.now();
    const WATCHDOG_MS = 30_000;
    const tick = () => {
      const now = Date.now();
      const delta = now - lastTickAt;
      lastTickAt = now;
      // Sleep detection — if the delta between ticks blew past the
      // expected window by `sleepDetectionMs`, assume we were asleep.
      // We still update last-interaction-at so the user doesn't have
      // to click to reset; the modal appearing on wake is enough.
      const sleptThrough =
        delta > WATCHDOG_MS + ENGAGEMENT_THRESHOLDS.sleepDetectionMs;
      const lastAt = lastAtRef.current;
      const nextEngagement: UserEngagement = sleptThrough
        ? "hard-idle"
        : engagementFor(lastAt, now);
      setState((prev) => {
        if (prev.engagement === nextEngagement) return prev;
        return { engagement: nextEngagement, lastInteractionAt: lastAt };
      });
    };
    const intervalId = window.setInterval(tick, WATCHDOG_MS);

    return () => {
      document.removeEventListener("pointerdown", onDiscrete);
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("keydown", onDiscrete);
      document.removeEventListener("scroll", onDiscrete, true);
      document.removeEventListener("wheel", onDiscrete);
      document.removeEventListener("touchstart", onDiscrete);
      window.clearInterval(intervalId);
    };
  }, []);

  return {
    engagement: state.engagement,
    lastInteractionAt: state.lastInteractionAt,
    bump
  };
}
